import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getActiveAuctions } from '../../../../models/auctions';
import i18n from '../../../../i18n';
import AuctionCardNew from '../Auction/AuctionCard';
import AuctionGroupCardNew from '../Auction/AuctionGroupCard';
import AuctionFilterBar from '../../helpers/AuctionFilterBar';
import { SaudiBranchCard, SellerCtaCard } from '../../helpers/Sliderctacards';
import { SkeletonAuctionCard } from '../../../helpers/skeletons/SkeletonAuctionCard';
import { Link } from 'react-router-dom';

let current_lng = localStorage.getItem('I18N_LANGUAGE');

export class OnlineAuction extends React.Component {
  constructor(props) {
    super(props);
    const params = new URLSearchParams(window.location.search);
    this.state = {
      auctions: [],
      loading: false,
      currentIndex: 0,
      visibleCount: 3,
      sortBy: params.get('sort') || 'order',
      timeFilter: params.get('timeFilter') || 'all',
      categoryFilter: params.get('category') || 'all',
      counts: { total: 0, active: 0, upcoming: 0, expired: 0 },
    };
    this.slideNext = this.slideNext.bind(this);
    this.slidePrev = this.slidePrev.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  componentDidMount() {
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
    this.loadAuctions();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize() {
    const w = window.innerWidth;
    let visibleCount = 3;
    if (w <= 600) visibleCount = 1;
    else if (w <= 992) visibleCount = 2;
    this.setState({ visibleCount, currentIndex: 0 });
  }

  /* ── URL query helpers ── */

  setQueryParam(key, value) {
    const url = new URL(window.location);
    if (value && value !== 'all' && value !== 'order') {
      url.searchParams.set(key, value);
    } else {
      url.searchParams.delete(key);
    }
    window.history.replaceState({}, '', url);
  }

  /* ── API call ── */

  // In loadAuctions(), after getting auctions, extract categories:
  async loadAuctions() {
    this.setState({ loading: true });
    try {
      let q = 'limit=100';
      const { timeFilter, categoryFilter } = this.state;

      if (timeFilter === 'current') q += '&type=active';
      else if (timeFilter === 'upcoming') q += '&type=upcoming';
      else if (timeFilter === 'previous') q += '&type=previous';

      const response = await getActiveAuctions(q);
      if (response) {
        let auctions = response.data || [];

        // ✅ Filter out Real Estate (cat 10) using categoryDetails.id
        auctions = auctions.filter(a => a?.categoryDetails?.id !== 10);

        // ✅ Filter by selected category using categoryDetails.id
        if (categoryFilter && categoryFilter !== 'all') {
          auctions = auctions.filter(
            a => String(a?.categoryDetails?.id) === String(categoryFilter)
          );
        }

        // Build category list from the FULL (cat-10-excluded) set,
        // before the category filter is applied — so pills don't disappear
        const fullSet = (response.data || []).filter(a => a?.categoryDetails?.id !== 10);
        const catMap = {};
        fullSet.forEach(auc => {
          const cd = auc.categoryDetails;
          if (cd && cd.id && cd.id !== 10) {
            if (!catMap[cd.id]) catMap[cd.id] = { ...cd, count: 0 };
            catMap[cd.id].count += 1;
          }
        });
        const categories = Object.values(catMap);

        const counts = {
          total: this.props.counts?.all,
          active: this.props.counts?.active,
          upcoming: this.props.counts?.upcoming,
          expired: this.props.counts?.previous
        };

        auctions = this.sortAuctions(auctions);
        this.setState({ auctions, counts, categories, loading: false, currentIndex: 0 });
      }
    } catch (e) {
      this.setState({ loading: false, auctions: [], categories: [] });
    }
  }
  sortAuctions(data) {
    if (!data.length) return data;
    const { sortBy } = this.state;
    switch (sortBy) {
      case 'newest':
        return data.sort((a, b) => new Date(b.created_at || b.start_date) - new Date(a.created_at || a.start_date));
      case 'oldest':
        return data.sort((a, b) => new Date(a.created_at || a.start_date) - new Date(b.created_at || b.start_date));
      case 'price_asc':
        return data.sort((a, b) => (a.current_amount || a.start_amount || 0) - (b.current_amount || b.start_amount || 0));
      case 'price_desc':
        return data.sort((a, b) => (b.current_amount || b.start_amount || 0) - (a.current_amount || a.start_amount || 0));
      default:
        return data.sort((a, b) => {
          const endA = new Date(a.end_date || a.reg_end_date || '9999-12-31');
          const endB = new Date(b.end_date || b.reg_end_date || '9999-12-31');
          return endA - endB; // soonest ending first, groups included
        });
    }
  }

  /* ── Filter handlers ── */

  handleSortChange = (sortBy) => {
    this.setQueryParam('sort', sortBy);
    this.setState({ sortBy }, () => this.loadAuctions());
  };

  handleTimeFilterChange = (timeFilter) => {
    this.setQueryParam('timeFilter', timeFilter);
    this.setState({ timeFilter }, () => {
      if (this.props.onTimeFilterChange) {
        this.props.onTimeFilterChange(timeFilter);
      }
      this.loadAuctions();
    });
  };

  handleCategoryChange = (categoryFilter) => {
    this.setQueryParam('category', categoryFilter);
    this.setState({ categoryFilter }, () => this.loadAuctions());
  };

  /* ── Slider nav ── */

  slideNext() {
    const totalSlides = this.state.auctions.length;
    const max = Math.max(0, totalSlides - this.state.visibleCount);
    this.setState((s) => ({ currentIndex: Math.min(s.currentIndex + 1, max) }));
  }

  slidePrev() {
    this.setState((s) => ({ currentIndex: Math.max(s.currentIndex - 1, 0) }));
  }

  render() {
    const { auctions, loading, currentIndex, visibleCount, counts } = this.state;
    const totalSlides = auctions.length + 2; // +2 CTA cards
    const maxIndex = Math.max(0, totalSlides - visibleCount);
    const slideWidth = 100 / visibleCount;
    const translateX = -(currentIndex * slideWidth);
    const dotCount = Math.max(0, totalSlides - visibleCount + 1);

    return (
      <>
        {loading ? (
          <div className="as-slider-outer">
            <SkeletonAuctionCard />
          </div>
        ) : (
          <>
            {/* ── Filter Bar ── */}
            <div className="as-slider-outer">
              <AuctionFilterBar
                counts={counts}
                sortBy={this.state.sortBy}
                timeFilter={this.state.timeFilter}
                categoryFilter={this.state.categoryFilter}
                onSortChange={this.handleSortChange}
                onTimeFilterChange={this.handleTimeFilterChange}
                onCategoryChange={this.handleCategoryChange}
                categories={this.state.categories}
                activeCategory={this.state.categoryFilter}
                showCount={true}
              />
            </div>

            {/* ── Slider ── */}
            <div className="as-slider-outer">
              <button
                className="as-arrow as-arrow--left"
                onClick={this.slidePrev}
                disabled={currentIndex === 0}
              >
                <ChevronLeft size={18} />
              </button>

              <div className="as-slider-track-wrap">
                <div
                  className="as-slider-track"
                  style={{ transform: `translateX(${translateX}%)` }}
                >
                  {auctions.map((auction, i) =>
                    !auction.is_a_group ? (
                      <div className="as-slide" key={auction.id || i}>
                        <AuctionCardNew auction={auction} auctionId={auction.id} />
                      </div>
                    ) : (auction?.categoryDetails?.id != 10 &&
                      <div className="as-slide" key={`group-${i}`}>
                        <AuctionGroupCardNew
                          group={auction}
                          total_items={auctions.length}
                          can_bid={true}
                          type={this.props.type}
                        />
                      </div>
                    )
                  )}
                  <div className="as-slide" key="cta-saudi">
                    <SaudiBranchCard />
                  </div>
                  <div className="as-slide" key="cta-seller">
                    <SellerCtaCard />
                  </div>
                </div>
              </div>

              <button
                className="as-arrow as-arrow--right"
                onClick={this.slideNext}
                disabled={currentIndex >= maxIndex}
              >
                <ChevronRight size={18} />
              </button>
            </div>


            {auctions.length > 4 && (
                    <div className="view-all-wrap">
                      <Link to={'/auctions?type=active&limit=9'} className="view-all-btn">
                        <span>{i18n.t('view_all')}</span>
                        {current_lng === 'ar' ? (
                          <ChevronLeft size={18} className="view-all-icon" />
                        ) : (
                          <ChevronRight size={18} className="view-all-icon" />
                        )}
                      </Link>
                    </div>
                  )}

            {/* ── Dots ── */}

          </>
        )}
      </>
    );
  }
}